/**
 * Subscription / plans / plafonds API. Gateway prefixes /api/v1/plans + /api/v1/subscription.
 */
import { api } from '@/lib/api/client';

export interface Plan {
  code: string;
  name: string;
  description: string | null;
  /** Monthly price in the EFFECTIVE currency (see `currency`/`price_fallback`). */
  price_monthly: number;
  /** Effective currency for the amounts on this object (lower-case ISO). */
  currency: string;
  ai_credits_monthly: number;
  overage_credit_price: number;
  limits: Record<string, number>;
  /** Boolean capability flags gating whole features (L-entitlements). */
  features: Record<string, boolean>;
  is_active: boolean;
  sort_order: number;
  // --- Multi-currency (ai-orchestrator) --------------------------------------
  /** True when the requested currency wasn't configured, so CAD was used. */
  price_fallback?: boolean;
  /** Monthly price per configured currency, major units ({cad:49, eur:35}). */
  prices?: Record<string, number>;
  /** Overage credit price per configured currency, major units. */
  overage_prices?: Record<string, number>;
  /** The catalogue's default currency (lower-case ISO; CAD). */
  default_currency?: string;
  /** Currencies the catalogue actually prices. */
  supported_currencies?: string[];
}

export interface UsageMetric {
  used: number;
  limit: number | null;
}
export interface CreditUsage {
  used: number;
  /** -1 means unlimited (superadmin / global-admin). */
  limit: number;
  /** null when the wallet is unlimited (no finite balance to show). */
  balance: number | null;
  overage_credits?: number;
  overage_cost?: number;
  /** True for superadmin/global-admin tenants with no spend cap. */
  unlimited?: boolean;
}
export interface MySubscription {
  subscription: {
    plan_code: string;
    status: string; // active | past_due | canceled
    started_at: string | null;
    current_period_end: string | null;
    cancel_at_period_end: boolean;
    can_cancel: boolean; // a paid Stripe subscription not already scheduled to end
    has_billing: boolean; // a Stripe customer exists → the billing portal is available
  };
  plan: Plan | null;
  usage: { projects: UsageMetric; ai_credits: CreditUsage };
  limits: Record<string, number>;
  /** Boolean capability flags of the current plan (L-entitlements). */
  features: Record<string, boolean>;
  /** Where to send the user to upgrade (feature/limit gating CTA). */
  upgrade_url: string;
  /** True for the platform superadmin tenant. */
  is_superadmin?: boolean;
  /** True when the tenant has no spend/credit cap (superadmin/global-admin). */
  unlimited?: boolean;
}

/**
 * Plan catalogue. Pass `currency` (lower-case ISO, e.g. `cad`) to price every
 * plan in that currency; the backend echoes the effective `currency` +
 * `price_fallback` per plan (CAD when the requested currency isn't configured).
 */
export function listPlans(currency?: string): Promise<Plan[]> {
  const q = currency ? `?currency=${encodeURIComponent(currency)}` : '';
  return api.get<Plan[]>(`/api/v1/plans${q}`);
}
export function getMySubscription(): Promise<MySubscription> {
  return api.get<MySubscription>('/api/v1/subscription/me');
}
/** Subscribe to `planCode`, billed in `currency` (defaults to CAD server-side). */
export function subscribe(
  planCode: string,
  currency?: string,
): Promise<{ success: boolean; plan_code?: string; checkout_required: boolean; checkout_url?: string }> {
  return api.post('/api/v1/subscription/subscribe', {
    plan_code: planCode,
    ...(currency ? { currency } : {}),
  });
}

/** The effective overage credit price for `currency` (per-currency map wins). */
export function overagePriceFor(plan: Plan, currency: string): number {
  const c = currency.toLowerCase();
  const mapped = plan.overage_prices?.[c];
  return typeof mapped === 'number' ? mapped : plan.overage_credit_price;
}

export interface CancelResult {
  success: boolean;
  cancel_at_period_end: boolean;
  current_period_end: string | null;
  status: string;
}
/** Résilier l'abonnement payant en fin de période (cancel_at_period_end). */
export function cancelSubscription(): Promise<CancelResult> {
  return api.post<CancelResult>('/api/v1/subscription/cancel', {});
}
/** Ouvre une session du portail de facturation Stripe et renvoie son url. */
export function openBillingPortal(): Promise<{ url: string }> {
  return api.post<{ url: string }>('/api/v1/billing/portal', {});
}
export function updatePlan(code: string, patch: Partial<Plan>): Promise<Plan> {
  return api.patch<Plan>(`/api/v1/plans/${encodeURIComponent(code)}`, patch);
}
export function createPlan(input: Partial<Plan> & { code: string; name: string }): Promise<Plan> {
  return api.post<Plan>('/api/v1/plans', input);
}
/**
 * Désactiver (soft-delete) un plan — `DELETE /api/v1/plans/{code}` (admin).
 * Le backend passe `is_active=false` (il ne détruit rien) : les abonnés actuels
 * conservent leur accès jusqu'au renouvellement, et le plan disparaît de la
 * grille de tarification. Renvoie `{ success: true }`. Équivalent à un
 * `updatePlan(code, { is_active: false })` mais via le verbe dédié.
 */
export function deactivatePlan(code: string): Promise<{ success: boolean }> {
  return api.delete<{ success: boolean }>(`/api/v1/plans/${encodeURIComponent(code)}`);
}

// ---- Stripe billable-catalog publishing (admin) --------------------------

export interface StripeCatalogItem {
  kind: 'plan' | 'pack';
  code: string;
  name: string;
  amount_cents: number;
  currency: string;
  interval: string | null; // 'month' for plans, null for one-time packs
  stripe_product_id: string | null;
  stripe_price_id: string | null;
  published: boolean;
  drift: boolean; // price changed since last publish → needs re-publish
  published_amount_cents: number | null;
}
export interface StripeCatalog {
  stripe_configured: boolean;
  livemode: boolean | null; // true=live keys, false=sandbox/test, null=unknown
  items: StripeCatalogItem[];
}
export interface PublishResultItem extends StripeCatalogItem {
  action: 'created' | 'updated' | 'unchanged';
}
export interface PublishResult {
  stripe_configured: boolean;
  livemode: boolean | null;
  items: PublishResultItem[];
}

export function getStripeCatalog(): Promise<StripeCatalog> {
  return api.get<StripeCatalog>('/api/v1/plans/stripe/catalog');
}
export function publishStripeCatalog(): Promise<PublishResult> {
  return api.post<PublishResult>('/api/v1/plans/stripe/publish', {});
}

/** Public (logged-out) trial + currency policy — GET /api/v1/plans/public-config (R2). */
export interface PublicPricingConfig {
  trial_on_signup: boolean;
  /** 0 when no signup trial is granted. */
  trial_days: number;
  trial_plan_code: string;
  card_required: boolean;
  default_currency: string;
  supported_currencies: string[];
}
export function getPublicPricingConfig(): Promise<PublicPricingConfig> {
  return api.get<PublicPricingConfig>('/api/v1/plans/public-config');
}
